import { money } from "../Subscription/money";
import { taxLineLabel, taxLinesOf } from "../Subscription/taxLines";
import { chargeOutcomeMessage } from "../Subscription/subscribeErrors";

/**
 * Display logic for the Billing page, kept pure so it can be tested without a
 * DOM — same shape as the Subscription/__tests__ helpers.
 *
 * The rule these encode: the SERVER decides what is metered and what a limit is.
 * A page that inferred "agent work counts toward your cap" would contradict
 * usage.services.enforcement the day either side changed, and would tell a
 * customer they are consuming an allowance nothing actually checks.
 */

export const UNLIMITED = "Unlimited";
export const INCLUDED = "Included";

/** A usage row as it should read. `metered=false` is never a progress bar. */
export function meterLabel({ count, limit }, metered = true) {
  if (!metered) return INCLUDED;
  if (limit === null || limit === undefined) return UNLIMITED;
  return `${count} of ${limit}`;
}

/** Percentage for a progress bar, or null when there is nothing to fill. */
export function meterPercent({ count, limit }, metered = true) {
  if (!metered) return null;
  if (limit === null || limit === undefined || Number(limit) <= 0) return null;
  return Math.min(100, Math.round((Number(count || 0) / Number(limit)) * 100));
}

/** Never negative: a customer over their cap has 0 left, not -3. */
export function remaining({ used, limit }) {
  if (limit === null || limit === undefined) return null;
  return Math.max(0, Number(limit) - Number(used || 0));
}

const KIND_LABEL = {
  subscription: "Subscription",
  overage: "Additional request",
  penalty: "Cancellation fee",
  charge: "Charge",
};

/** One invoice row, ready to render. */
export function invoiceRow(row) {
  if (!row) return null;
  const failed = String(row.status || "").toLowerCase() !== "paid";
  return {
    id: row.id,
    date: row.date ? new Date(row.date) : null,
    kind: row.kind,
    kindLabel: KIND_LABEL[row.kind] || "Charge",
    description: row.description,
    amount: `$${money(row.amount)}`,
    subtotal: `$${money(row.subtotal)}`,
    // One entry per tax charged ("HST", or "GST 5%" and "PST 7%"); none if exempt.
    taxLines: taxLinesOf(row).map((line, i, all) => ({
      label: taxLineLabel(line, all.length),
      amount: `$${money(line.amount)}`,
    })),
    taxEstimated: Boolean(row.tax_estimated),
    failed,
    // A declined attempt is a row too; say why rather than showing a bare "failed".
    statusLabel: failed ? (row.outcome === "declined" ? "Declined" : "Not paid") : "Paid",
    reference: row.helcim_transaction_id || null,
    currency: (row.currency || "cad").toUpperCase(),
    // A plan bought under an offer: say so on the receipt row.
    discount: Number(row.discount || 0) > 0
      ? `Includes $${money(row.discount)} off${row.discount_label ? ` — ${row.discount_label}` : ""}`
      : null,
  };
}

/**
 * What the confirm dialog says before a plan change is charged.
 *
 * The credit is the whole reason this dialog exists: an annual plan is paid a year
 * at a time, so without saying what happens to the months already bought, an
 * upgrade looks like being charged twice for the same period.
 */
export function planChangeCopy(quote) {
  if (!quote) return null;
  const status = quote.quote || quote.status;

  if (status === "downgrade") {
    return {
      title: `Switch to ${quote.new_tier_title}`,
      lines: [
        "Nothing is charged today.",
        `Your current plan is paid for, so ${quote.new_tier_title} starts when that term ends.`,
      ],
      total: null,
      okText: "Schedule the change",
    };
  }

  const credit = Number(quote.credit || 0);
  const lines = [`${quote.new_tier_title} — ${quote.term_months || 12} months: $${money(quote.plan_subtotal)}`];
  if (quote.offer && Number(quote.offer.discount || 0) > 0) {
    lines.push(`Includes ${quote.offer.label || "your offer"}: −$${money(quote.offer.discount)}`);
  }
  if (credit > 0) {
    const d = quote.credit_detail || {};
    lines.push(
      `Credit for the ${d.remaining_days || ""} days left on your current plan: −$${money(quote.credit)}`
    );
  }
  const taxes = taxLinesOf(quote);
  taxes.forEach((line) => lines.push(`${taxLineLabel(line, taxes.length)}: $${money(line.amount)}`));

  return {
    title: `Upgrade to ${quote.new_tier_title}`,
    lines,
    total: `$${money(quote.total)}`,
    okText: `Pay $${money(quote.total)}`,
  };
}


/**
 * The banner to show above the plan, or null for "say nothing".
 *
 * ``chargeOutcomeMessage`` returns a {kind, text} PAIR, not a string. Handing
 * that object straight to <Alert message={...}> is what turned this page white
 * on prod (React error #31, "Objects are not valid as a React child (found:
 * object with keys {kind, text})"). Unwrapping it here, where it is tested,
 * rather than at the call site.
 *
 * Two states get no banner at all:
 *   active — the plan panel already says so; a green bar every visit is noise.
 *   none   — the viewer simply has no subscription of their own for this office
 *            (a super-admin looking at someone else's practice). The default
 *            branch of chargeOutcomeMessage would tell them we are "confirming
 *            your payment", which is about a charge that does not exist.
 */
export function outcomeAlert(state) {
  if (!state || state === "active" || state === "none") return null;
  const out = chargeOutcomeMessage(state) || {};
  const TYPE = { success: "success", error: "error", info: "info", wait: "info" };
  return { type: TYPE[out.kind] || "info", text: String(out.text || "") };
}
